<template>
	<div>
		<b-breadcrumb>Channels</b-breadcrumb>
		<b-alert :show="!!error" variant="danger">{{error&&(error.error||error.toString())}}</b-alert>
		<b-form-row class="mb-2">
			<b-col md="3" sm="6" class="mb-2 mb-md-0">
				<b-input v-model="q" placeholder="Search..." @input="loadChannels()"></b-input>
			</b-col>
			<b-col md="3" sm="6" class="mb-2 mb-md-0">
				<b-form-select v-model="serviceId" :options="serviceOptions" @change="applyFilters()"></b-form-select>
			</b-col>
			<b-col md="2" sm="6" class="mb-2 mb-md-0">
				<b-form-select v-model="status" :options="statusOptions" @change="applyFilters()"></b-form-select>
			</b-col>
			<b-col md="2" sm="6" class="mb-2 mb-md-0">
				<b-form-select v-model="verificationStatus" :options="verificationOptions" @change="applyFilters()"></b-form-select>
			</b-col>
			<b-col md="2" sm="6">
				<div style="position: relative;">
					<b-input
						v-model="ownerQuery"
						placeholder="Owner..."
						autocomplete="off"
						@input="ownerSearch()"
						@keydown.down.prevent="ownerMove(1)"
						@keydown.up.prevent="ownerMove(-1)"
						@keydown.enter.prevent="ownerEnter()"
						@keydown.esc="ownerSuggestions = []"
						@blur="ownerBlur()"
					></b-input>
					<b-list-group v-if="ownerSuggestions.length" style="position: absolute; z-index: 10; width: 100%;">
						<b-list-group-item button v-for="(u, idx) in ownerSuggestions" :key="u.id" :active="idx == ownerHighlight" @mousedown.prevent="selectOwner(u)" @mouseenter="ownerHighlight = idx">{{u.name}}</b-list-group-item>
					</b-list-group>
				</div>
			</b-col>
		</b-form-row>
		<b-table small striped hover :items="channels" :busy="loading>0" :fields="['avatar','name','owner','info','status',{ key: 'emote_count', label: 'Emotes' },{ key: 'membership_level_count', label: 'Levels' },{ label: '', key: 'actions', thClass: 'text-right', tdClass: 'text-right' }]" :tbody-tr-class="rowClass">
			<template #cell(avatar)="data">
				<b-avatar size="3em" :src="data.item.avatar_url||data.item.service_icon_url" badge-variant=""  badge-offset="-0.4em">
					<template #badge><b-avatar :src="data.item.service_icon_url||null" size="1.3em" class="service_avatar" square :title="data.item.service_name"></b-avatar></template>
				</b-avatar>
			</template>
			<template #cell(name)="data">
				<a :href="data.item.info&&data.item.info.url" target="_blank">{{data.item.title}}</a>
				<b-icon icon="x" variant="danger" v-if="data.item.verification_status != 'verified'"></b-icon><br />
				<code>{{data.item.channel_token}}</code><br />
				<small>{{data.item.service_name}}</small>
			</template>
			<template #cell(owner)="data">
				<a href="#" v-if="data.item.user_id" @click.prevent="loginAsUser(data.item.user_id)" title="Log in as this user">{{data.item.owner_username}}</a>
				<span v-else>{{data.item.owner_username}}</span>
			</template>
			<template #cell(info)="data">
				<b-badge pill :variant="data.item.loading > 0 ? 'warning' : (data.item.error ? 'danger' : (data.value.numLive>0?'success':'secondary'))" v-if="data.item.status == 'enabled'" :title="data.value.numLive>0?'live':'offline'" v-b-tooltip.hover>{{typeof data.value.numViewers == 'number' ? data.value.numViewers : '-'}}</b-badge>
			</template>
			<template #cell(actions)="data">
				<b-button-group size="sm">
					<b-button variant="info" :to="{ name: 'Channel', params: { userId: data.item.user_id, serviceId: data.item.service_id, token: data.item.channel_token } }" v-b-tooltip.hover.left.v-info title="View details"><b-icon icon="eye"></b-icon></b-button>
					<b-button variant="primary" v-b-tooltip.hover.v-primary title="Edit channel" @click="editChannel(data.item.user_id, data.item.service_id, data.item.channel_token)"><b-icon icon="pencil"></b-icon></b-button>
					<b-button variant="danger" v-b-tooltip.hover.right.v-danger title="Delete channel" @click="deleteChannel(data.item.user_id, data.item.service_id, data.item.token)"><b-icon icon="trash"></b-icon></b-button>
				</b-button-group>
			</template>
			<template #cell(status)="data">
				{{data.item.status}}
			</template>
		</b-table>
		<pager :per-page="perPage" :skip="skip" :total="total" @change="setPage"></pager>
		<b-modal ref="editModal">
			<b-form @submit.stop.prevent="saveChannel" id="channelEditForm" v-if="curChannel">
				<b-form-group label="Service">
					<b-form-input readonly v-model="curChannel.service_name"></b-form-input>
				</b-form-group>
				<b-form-group label="Channel ID">
					<b-form-input v-model="curChannel.token" placeholder="Token" required readonly></b-form-input>
				</b-form-group>
				<b-form-group label="Channel Name">
					<b-form-input v-model="curChannel.title" placeholder="Name" required readonly></b-form-input>
				</b-form-group>
				<b-form-group label="Owner">{{curChannel.username}}</b-form-group>
				<b-form-group label="Status">
					<b-form-checkbox switch v-model="curChannel.verification_status" value="verified" unchecked-value="unverified">Verified</b-form-checkbox>
				</b-form-group>
				<b-form-group label="Session">
					<b-textarea v-model="curChannel.session" placeholder="Session" readonly rows="6"></b-textarea>
				</b-form-group>
				<b-form-group label="Emotes">
					<b-button size="sm" variant="secondary" :disabled="emotesLoading>0" @click="fetchChannelEmotes()">
						<b-spinner v-if="emotesLoading>0" small class="mr-1"></b-spinner>
						Fetch emotes
					</b-button>
					<b-alert variant="danger" :show="!!emotesError" class="mt-2 mb-0">{{emotesError&&(emotesError.error||emotesError.toString())}}</b-alert>
					<div v-if="channelEmotes" class="d-flex flex-wrap mt-2" style="gap: .75rem;">
						<span v-if="!channelEmotes.length" class="text-muted small">No emotes found.</span>
						<div v-for="e in channelEmotes" :key="`${e.id}-${e.label}`" class="text-center" style="width: 56px;" :title="(e.shortcuts||[]).join(', ')">
							<img :src="e.imageUrl" :alt="e.label" style="max-width: 32px; max-height: 32px;" />
							<div class="small text-truncate">{{e.label}}</div>
						</div>
					</div>
				</b-form-group>
				<b-form-group label="Channel data sync (debug)">
					<b-button size="sm" variant="secondary" :disabled="syncLoading>0" @click="syncChannelData()">
						<b-spinner v-if="syncLoading>0" small class="mr-1"></b-spinner>
						Sync now
					</b-button>
					<b-alert variant="danger" :show="!!syncError" class="mt-2 mb-0">{{syncError&&(syncError.error||syncError.toString())}}</b-alert>
					<div v-if="syncResult" class="mt-2">
						<div v-for="section in syncSections" :key="section.key" class="mb-2">
							<strong>{{section.label}}:</strong>
							<b-alert v-if="syncResult.errors[section.key]" show variant="danger" class="mb-0 mt-1 py-1 px-2 small">{{syncResult.errors[section.key]}}</b-alert>
							<b-alert v-else-if="syncResult.skipped[section.key]" show variant="warning" class="mb-0 mt-1 py-1 px-2 small">Skipped: {{syncResult.skipped[section.key]}}</b-alert>
							<pre v-else class="mb-0 mt-1 p-2 small" style="max-height: 200px; overflow: auto; background: rgba(255,255,255,.05);">{{JSON.stringify(section.value, null, 2)}}</pre>
						</div>
					</div>
				</b-form-group>
			</b-form>
			<b-alert variant="danger" :show="!!channelError">{{channelError}}</b-alert>
			<template #modal-footer="{ ok, cancel }">
				<div class="w-100">
					<div class="float-right">
						<b-spinner v-show="channelLoading>0" small label="Spinning" class="mr-3"></b-spinner>
						<b-button size="sm" variant="success" type="submit" form="channelEditForm" class="mr-2">
							Save
						</b-button>
						<b-button size="sm" @click="cancel()">
							Cancel
						</b-button>
					</div>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import Pager from '@/components/Pager.vue';
import debounce from 'debounce';

export default {
	name: 'channels',
	components: {
		Pager,
	},
	data() { return {
		channels: [],
		error: null,
		loading: 0,
		total: 0,
		skip: 0,
		perPage: 10,
		q: undefined,
		serviceId: null,
		status: null,
		verificationStatus: null,
		ownerUser: null,
		ownerQuery: '',
		ownerSuggestions: [],
		ownerHighlight: -1,
		ownerSearching: 0,
		services: [],
		curChannel: null,
		channelError: null,
		channelLoading: 0,
		channelEmotes: null,
		emotesError: null,
		emotesLoading: 0,
		syncResult: null,
		syncError: null,
		syncLoading: 0,
		userSuggestions: [],
		userSearching: 0,
	}},
	computed: {
		serviceOptions() {
			return [{ value: null, text: 'All services' }, ...this.services.map(s => ({ value: s.id, text: s.name }))];
		},
		statusOptions() {
			return [{ value: null, text: 'Any status' }, { value: 'enabled', text: 'Enabled' }, { value: 'disabled', text: 'Disabled' }];
		},
		verificationOptions() {
			return [{ value: null, text: 'Any verification' }, { value: 'verified', text: 'Verified' }, { value: 'unverified', text: 'Unverified' }];
		},
		// syncServiceChannelData syncs (and this echoes) a whole service's channels at
		// once; membershipLevels/emotes.byChannel are keyed by token, so pick out just
		// this one channel's slice for display. giftTypes/emotes.global stay service-wide.
		syncSections() {
			if(!this.syncResult || !this.curChannel) return [];
			return [
				{ key: 'giftTypes', label: 'Gift types', value: this.syncResult.giftTypes },
				{ key: 'membershipLevels', label: 'Membership levels', value: this.syncResult.membershipLevels?.[this.curChannel.token] || [] },
				{ key: 'emotes', label: 'Emotes', value: this.syncResult.emotes?.byChannel?.[this.curChannel.token] || [] },
			];
		},
	},
	created() {
		this.skip = parseInt(this.$route.query.skip||0)||0;
		this.perPage = parseInt(this.$route.query.perPage||10)||10;
		this.$api.get('admin/services').then(services => this.services = services).catch(err => this.error = err);
		this.loadChannels();
	},
	watch: {
		'$route': 'loadChannels',
		'q': 'loadChannels',
	},
	methods: {
		setPage($event) {
			this.skip = $event.skip;
			this.perPage = $event.perPage;
			this.$router.push({ query: { ...this.$route.query, skip: $event.skip || undefined, perPage: $event.perPage == 10 ? undefined : $event.perPage } });
		},
		applyFilters() {
			this.skip = 0;
			this.loadChannels();
		},
		async loginAsUser(userId) {
			const { token } = await this.$api.post(`auth/${encodeURIComponent(userId)}`);
			this.$auth.pushToken(token);
			this.$router.push('/');
		},
		selectOwner(user) {
			this.ownerUser = user;
			this.ownerQuery = user.name;
			this.ownerSuggestions = [];
			this.ownerHighlight = -1;
			this.applyFilters();
		},
		ownerMove(delta) {
			if(!this.ownerSuggestions.length) return;
			this.ownerHighlight = Math.max(0, Math.min(this.ownerSuggestions.length - 1, this.ownerHighlight + delta));
		},
		ownerEnter() {
			if(!this.ownerSuggestions.length) return;
			this.selectOwner(this.ownerSuggestions[this.ownerHighlight >= 0 ? this.ownerHighlight : 0]);
		},
		ownerBlur() {
			// let a suggestion click (mousedown) land before hiding the list
			this.ownerSuggestions = [];
			this.ownerHighlight = -1;
		},
		ownerSearch: debounce(function() {
			if(!this.ownerQuery) {
				this.ownerSuggestions = [];
				this.ownerHighlight = -1;
				if(this.ownerUser) { this.ownerUser = null; this.applyFilters(); }
				return;
			}
			this.ownerSearching++;
			this.$api.get('users', { q: this.ownerQuery }).then(results => {
				this.ownerSuggestions = results.results.map(r => ({ ...r, name: r.name||r.username||r.token }));
				this.ownerHighlight = -1;
			}).catch(err => {
				this.ownerSuggestions = [];
				this.error = err.error||err;
			}).finally(() => this.ownerSearching--);
		}, 300),
		loadChannels() {
			this.loading++;
			this.error = null;
			const params = { q: this.q||'', limit: this.perPage, skip: this.skip };
			if(this.serviceId) params.service_id = this.serviceId;
			if(this.status) params.status = this.status;
			if(this.verificationStatus) params.verification_status = this.verificationStatus;
			if(this.ownerUser) params.user_id = this.ownerUser.id;
			this.$api.get('admin/userChannels', params).then(channels => {
				// Kept as a local reference: a slower, now-stale loadChannels() call could
				// otherwise resolve after a newer one already swapped this.channels for a
				// different (possibly shorter) array, indexing past its end.
				const rows = this.channels = channels.results.map(c => ({ ...c, loading: true }));
				this.total = channels.total;
				Promise.allSettled(channels.results.map(c => c.status == 'enabled' && this.$api.get(`channel/${encodeURIComponent(c.service_id)}/${encodeURIComponent(c.channel_token)}/info`))).then(settled => {
					for(let i = 0; i < settled.length; i++) {
						rows[i].loading = false;
						if(settled[i].status == 'fulfilled') {
							this.$set(rows[i], 'info', settled[i].value);
							this.$set(rows[i], 'error', null);
						} else {
							this.$set(rows[i], 'info', null);
							this.$set(rows[i], 'error', settled[i].reason);
						}
					}
				});
			}).catch(err => this.error = err).finally(() => this.loading--);
		},
		deleteChannel(service_id, token) {
			this.loading++;
			this.error = null;
			this.$api.delete(`admin/channel/${encodeURIComponent(service_id)}/${encodeURIComponent(token)}`).then(() => {
				this.$bvToast.toast(`Deleted channel`, { title: 'Success', variant: 'success', solid: true });
				this.loadChannels();
			}, err => this.error = err).finally(() => {
				this.loading--;
			});
		},
		async editChannel(user_id, service_id, token) {
			this.$refs.editModal.show();
			this.channelEmotes = null;
			this.emotesError = null;
			this.syncResult = null;
			this.syncError = null;
			try {
				this.channelLoading++;
				this.channelError = null;
				const channel = await this.$api.get(`admin/userChannel/${encodeURIComponent(user_id)}/${encodeURIComponent(service_id)}/${encodeURIComponent(token)}`);
				channel.owner_username = channel.owner_username || '';
				channel.session = JSON.stringify(channel.session);
				this.curChannel = channel;
			} catch(e) {
				this.channelError = e;
			} finally {
				this.channelLoading--;
			}
		},
		async fetchChannelEmotes() {
			this.emotesLoading++;
			this.emotesError = null;
			try {
				this.channelEmotes = await this.$api.get(`admin/userChannel/${encodeURIComponent(this.curChannel.user_id)}/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/emotes`);
			} catch(e) {
				this.emotesError = e;
			} finally {
				this.emotesLoading--;
			}
		},
		async syncChannelData() {
			this.syncLoading++;
			this.syncError = null;
			try {
				this.syncResult = await this.$api.post(`admin/userChannel/${encodeURIComponent(this.curChannel.user_id)}/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}/sync`);
			} catch(e) {
				this.syncError = e;
			} finally {
				this.syncLoading--;
			}
		},
		saveChannel() {
			this.channelLoading++;
			this.channelError = null;
			this.$api.patch(`admin/userChannel/${encodeURIComponent(this.curChannel.user_id)}/${encodeURIComponent(this.curChannel.service_id)}/${encodeURIComponent(this.curChannel.token)}`, {
				verification_status: this.curChannel.verification_status,
			}).then(() => {
				this.$refs.editModal.hide();
				this.$bvToast.toast('Channel updated', {
					title: 'Success',
					variant: 'success',
					solid: true
				});
				this.loadChannels();
			}, err => this.channelError = err).finally(() => {
				this.channelLoading--;
			});
		},
		rowClass(item, type) {
			if (!item || type !== 'row') return;
			return item.num_users == 0 && 'table-danger text-danger';
		},
		setChannelUser(user) {
			this.curChannel.owner_username = user.username;
			this.curChannel.owner_user_id = user.id;
		},
		clearOwner() {
			this.curChannel.owner_username = '';
			this.curChannel.owner_user_id = null;
		},
		userSearch: debounce(function() {
			this.userSearching++;
			this.userSuggestions = [];
			this.$api
				.get('users', { q: this.curChannel.owner_username })
				.then(results => {
					this.userSuggestions = results.results.map(r => ({ ...r, name: r.name||r.token }));
				}).catch(err => {
					this.userSuggestions = [];
					this.error = err.error;
				}).finally(() => {
					this.userSearching--;
				});
		}, 300),
	},
};
</script>
